import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';
import useToast from 'services/Hooks/useToast';
import { useSelector } from 'react-redux';
import { dealerIdsQueryStringBuilder } from 'services/Utilities/TableData';

const normalizeDealerIds = dealerIds => dealerIds?.filter(Boolean) ?? [];

export const useDealer = (dealerIdOrOptions, queryOptions = {}) => {
  const { errorMessage } = useToast();
  const isLoggedIn = useSelector(state => state.AccountReducer.isLoggedIn);
  const resolvedQueryOptions =
    dealerIdOrOptions && typeof dealerIdOrOptions === 'object' && !Array.isArray(dealerIdOrOptions)
      ? dealerIdOrOptions
      : queryOptions;
  const { enabled: enabledOverride, ...restOptions } = resolvedQueryOptions ?? {};

  const query = useQuery({
    queryKey: ['dealer'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/dealers/current');
      return response.data;
    },
    enabled: isLoggedIn === true && (enabledOverride ?? true),
    throwOnError: errorMessage,
    ...restOptions,
  });
  return query;
};

export const useDealers = (isLogged, isEnabled) => {
  const { errorMessage } = useToast();
  const currentUserId = useSelector(state => state.AccountReducer.profile?.id);

  const query = useQuery({
    queryKey: ['dealers', currentUserId],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/dealers/available');
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged && !!isEnabled,
    refetchOnMount: 'always',
    staleTime: 0,
  });
  return query;
};

export const useDsmDealerComplianceReports = (isLogged, dealerIds = []) => {
  const { errorMessage } = useToast();
  const normalizedDealerIds = normalizeDealerIds(dealerIds);
  const requestOptions = normalizedDealerIds.length
    ? {
        params: { dealerIds: normalizedDealerIds },
        paramsSerializer: params => dealerIdsQueryStringBuilder(params.dealerIds),
      }
    : undefined;

  const query = useQuery({
    queryKey: ['dealer-dsm-compliance-report', normalizedDealerIds.join('|')],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/dealers/dsm-compliance-report', requestOptions);
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
  });
  return query;
};

export const useDsmCertificationReports = (isLogged, dealerIds = []) => {
  const { errorMessage } = useToast();
  const normalizedDealerIds = normalizeDealerIds(dealerIds);
  const requestOptions = normalizedDealerIds.length
    ? {
        params: { dealerIds: normalizedDealerIds },
        paramsSerializer: params => dealerIdsQueryStringBuilder(params.dealerIds),
      }
    : undefined;

  const query = useQuery({
    queryKey: ['dealer-dsm-certification-report', normalizedDealerIds.join('|')],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/dealers/dsm-certification-report', requestOptions);
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
  });
  return query;
};

export const useDemoDealers = (isLogged, isDemo) => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['demo-dealers'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/dealers/demo-available');
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged && !!isDemo,
  });
  return query;
};

/**
 * Fetch dealer by ID
 * @param {string} dealerId - The dealer ID
 */
export const useCertificationToolDealer = (dealerId, queryOptions = {}) => {
  const { enabled: enabledOverride, ...restOptions } = queryOptions ?? {};

  return useQuery({
    queryKey: ['dealer', dealerId],
    queryFn: async () => {
      if (!dealerId) {
        throw new Error('Dealer ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/dealers/${dealerId}`);
      return response.data;
    },
    enabled: !!dealerId && (enabledOverride ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...restOptions,
  });
};

// BRP CLUB section
export const useBrpClubDealer = dealerId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['brp-club-dealer', dealerId],
    queryFn: async () => {
      if (!dealerId) {
        throw new Error('Dealer ID is required');
      }
      const response = await axios.get(`${HOST}/api/dealer-portal/brp-club/dealers/current`, {
        params: { dealerNumber: dealerId },
      });
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!dealerId,
  });
  return query;
};
